import { useEffect, useState } from 'react';
import { resolveCampaignAvatar, type CampaignAvatarInput } from '@/lib/campaigns/resolveCampaignAvatar';
import { cn } from '@/lib/utils';

const SIZES = {
  md: 'w-12 h-12 text-sm',
  lg: 'w-14 h-14 text-base',
} as const;

/**
 * Round campaign avatar with the shared rule (logo → first channel thumbnail →
 * initials, see resolveCampaignAvatar). A broken image falls back to the
 * initials instead of an empty circle.
 */
export function CampaignAvatar({ campaign, size = 'md', className }: {
  campaign: CampaignAvatarInput;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const { src, initials } = resolveCampaignAvatar(campaign);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={campaign.name || ''}
        onError={() => setFailed(true)}
        className={cn('rounded-full object-cover border-2 border-emerald-500/40 flex-shrink-0', SIZES[size], className)}
        data-testid="campaign-avatar-image"
      />
    );
  }
  return (
    <div
      aria-hidden="true"
      className={cn(
        'rounded-full flex items-center justify-center font-bold border-2 bg-primary/20 border-primary/30 text-primary flex-shrink-0',
        SIZES[size],
        className,
      )}
      data-testid="campaign-avatar-initials"
    >
      {initials}
    </div>
  );
}
